import React, { Suspense, SuspenseProps, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { setPageTitle } from '@/utils/dom';

interface MetaProps {
  title: string;
  key: string;
}

export interface RouteItem {
  index?: boolean;
  caseSensitive?: boolean;
  path?: string;
  redirect?: string;
  element?: React.ReactNode;
  children?: RouteItem[];
  meta?: MetaProps;
}

const PageTitle: React.FC<RouteItem> = item => {
  useEffect(() => {
    setPageTitle(item?.meta?.title || '');
  }, [item?.meta?.title]);
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{item.element}</>;
};

const renderRoute = (route: RouteItem, index: number) => {
  if (route.redirect) {
    return (
      <Route
        key={`${route.redirect}redirect`}
        path={route.path}
        element={<Navigate replace to={route.redirect} />}
      />
    );
  }

  const { children } = route;
  if (!children || children.length === 0) {
    return (
      <Route
        key={route.index ? route.meta?.key : route.path || index}
        index={Boolean(route.index)}
        path={route.path}
        element={<PageTitle {...route} />}
      />
    );
  }

  return (
    <Route
      key={route.index ? route.meta?.key : route.path || index}
      element={route.element}>
      {children.map((route, index) => renderRoute(route, index))}
    </Route>
  );
};

interface RenderRouterProps {
  routes: RouteItem[];
  fallback?: SuspenseProps['fallback'];
}

const RenderRouter: React.FC<RenderRouterProps> = ({
  routes,
  fallback = null,
}) => {
  if (!routes?.length) {
    return null;
  }
  return (
    <Suspense fallback={fallback}>
      <Routes>{routes.map((route, index) => renderRoute(route, index))}</Routes>
    </Suspense>
  );
};

export default RenderRouter;
